import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Avatar,
  Button,
  Chip,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack,
  Delete,
  People,
  Message,
  School,
  Edit,
  Search,
  Person,
  CheckCircle,
} from '@mui/icons-material';
import adminService from '../../../services/admin.service';

interface GroupDetails {
  chatId: string;
  groupName: string;
  groupDescription?: string;
  groupAvatar?: string;
  groupSchoolName: string;
  participants: Array<{
    userId: string;
    fullname: string;
    profileImage: string;
  }>;
  messageCount: number;
  createdAt: string;
  isActive: boolean;
  createdBy: string;
}

interface GroupMessage {
  messageId: string;
  senderId: string;
  senderFullname: string;
  senderProfileImage: string;
  content: string;
  messageType: string;
  createdAt: string;
  isDeleted: boolean;
}

const GroupDetail: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();

  const [group, setGroup] = useState<GroupDetails | null>(null);
  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [activateDialog, setActivateDialog] = useState(false);
  const [messagesDialog, setMessagesDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [messageSearch, setMessageSearch] = useState('');

  // Edit form state
  const [editForm, setEditForm] = useState({
    groupName: '',
    groupDescription: '',
    groupAvatar: '',
    groupSchoolName: '',
  });

  useEffect(() => {
    if (chatId) {
      fetchGroupDetails();
    }
  }, [chatId]);

  const fetchGroupDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminService.getGroupDetail(chatId!);
      if (response.status) {
        console.log('Group data:', response.data);
        console.log('isActive value:', response.data.isActive, 'type:', typeof response.data.isActive);
        
        // Backend'den isActive gelmiyorsa, varsayılan olarak true kabul et
        const groupData = {
          ...response.data,
          isActive: response.data.isActive !== undefined ? response.data.isActive : true
        };
        
        setGroup(groupData);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Grup bilgileri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupMessages = async () => {
    try {
      const response = await adminService.getGroupMessages(chatId!, {
        page: 1,
        limit: 100,
        includeDeleted: false,
      });
      if (response.status) {
        // Backend sender objesi içinde dönüyor, frontend formatına dönüştür
        const formattedMessages = (response.data.messages || []).map((msg: any) => ({
          messageId: msg.messageId,
          senderId: msg.sender?.userId || msg.senderId,
          senderFullname: msg.sender?.fullname || msg.senderFullname,
          senderProfileImage: msg.sender?.profileImage || msg.senderProfileImage,
          content: msg.content,
          messageType: msg.messageType,
          createdAt: msg.timestamp || msg.createdAt,
          isDeleted: msg.isDeleted,
        }));
        setMessages(formattedMessages);
      }
    } catch (err: any) {
      alert('Mesajlar yüklenirken hata oluştu.');
    }
  };

  const handleDeleteGroup = async () => {
    setActionLoading(true);
    try {
      await adminService.deleteGroup(chatId!);
      alert('Grup başarıyla silindi!');
      navigate('/admin/groups');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Grup silinirken hata oluştu.');
    } finally {
      setActionLoading(false);
      setDeleteDialog(false);
    }
  };

  const handleActivateGroup = async () => {
    setActionLoading(true);
    try {
      await adminService.activateGroup(chatId!);
      alert('Grup başarıyla aktifleştirildi!');
      setActivateDialog(false);
      await fetchGroupDetails(); // Grubu yeniden yükle
    } catch (err: any) {
      alert(err.response?.data?.message || 'Grup aktifleştirilirken hata oluştu.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!window.confirm('Bu mesajı silmek istediğinizden emin misiniz?')) return;

    try {
      await adminService.deleteGroupMessage(chatId!, messageId);
      alert('Mesaj silindi!');
      await fetchGroupMessages();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Mesaj silinirken hata oluştu.');
    }
  };

  const handleOpenMessages = async () => {
    setMessagesDialog(true);
    await fetchGroupMessages();
  };

  const handleOpenEditDialog = () => {
    if (group) {
      setEditForm({
        groupName: group.groupName || '',
        groupDescription: group.groupDescription || '',
        groupAvatar: group.groupAvatar || '',
        groupSchoolName: group.groupSchoolName || '',
      });
      setEditDialog(true);
    }
  };

  const handleUpdateGroup = async () => {
    setActionLoading(true);
    try {
      await adminService.updateGroup(chatId!, {
        groupName: editForm.groupName,
        groupDescription: editForm.groupDescription,
        groupAvatar: editForm.groupAvatar,
        groupSchoolName: editForm.groupSchoolName,
      });
      alert('Grup başarıyla güncellendi!');
      setEditDialog(false);
      await fetchGroupDetails(); // Grubu yeniden yükle
    } catch (err: any) {
      alert(err.response?.data?.message || 'Grup güncellenirken hata oluştu.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !group) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Grup bulunamadı.'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/groups')} sx={{ mt: 2 }}>
          Geri Dön
        </Button>
      </Container>
    );
  }

  const filteredMessages = messages.filter((msg) =>
    (msg.content?.toLowerCase() || '').includes(messageSearch.toLowerCase()) ||
    (msg.senderFullname?.toLowerCase() || '').includes(messageSearch.toLowerCase())
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <IconButton onClick={() => navigate('/admin/groups')} sx={{ mb: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          Grup Detayları
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Group Info Card */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar
                src={group.groupAvatar}
                sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: '#667eea' }}
              >
                <People sx={{ fontSize: 50 }} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {group.groupName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {group.chatId}
              </Typography>

              {group.isActive === false ? (
                <Chip label="Pasif" color="default" size="small" sx={{ mt: 1 }} />
              ) : (
                <Chip label="Aktif" color="success" size="small" sx={{ mt: 1 }} />
              )}

              <Divider sx={{ my: 3 }} />

              {/* Stats */}
              <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight="bold">
                    {group.participants?.length || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Üye
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight="bold">
                    {group.messageCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Mesaj
                  </Typography>
                </Box>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Message />}
                  onClick={handleOpenMessages}
                  fullWidth
                >
                  Mesajları Görüntüle
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Edit />}
                  onClick={handleOpenEditDialog}
                  fullWidth
                >
                  Grubu Düzenle
                </Button>
                
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => setActivateDialog(true)}
                  fullWidth
                >
                  Grubu Aktifleştir
                </Button>
                
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteDialog(true)}
                  fullWidth
                >
                  Grubu Sil
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Details & Members */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Grup Bilgileri
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <School color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Okul
                    </Typography>
                    <Typography variant="body2">{group.groupSchoolName || '-'}</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Grup Açıklaması
                </Typography>
                <Typography variant="body2">
                  {group.groupDescription || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Oluşturulma Tarihi
                </Typography>
                <Typography variant="body2">
                  {new Date(group.createdAt).toLocaleString('tr-TR')}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Members List */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Grup Üyeleri ({group.participants?.length || 0})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <List>
              {group.participants?.map((participant) => (
                <ListItem key={participant.userId}>
                  <ListItemAvatar>
                    <Avatar src={participant.profileImage}>
                      {participant.fullname[0]?.toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={participant.fullname}
                    secondary={participant.userId}
                  />
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/admin/users/${participant.userId}`)}
                  >
                    <Person />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Grubu Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu grubu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>İptal</Button>
          <Button
            onClick={handleDeleteGroup}
            color="error"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Dialog */}
      <Dialog open={activateDialog} onClose={() => setActivateDialog(false)}>
        <DialogTitle>Grubu Aktifleştir</DialogTitle>
        <DialogContent>
          <Typography>
            Bu grubu aktifleştirmek istediğinizden emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialog(false)}>İptal</Button>
          <Button
            onClick={handleActivateGroup}
            color="success"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Aktifleştir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grubu Düzenle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Grup Adı"
              fullWidth
              value={editForm.groupName}
              onChange={(e) => setEditForm({ ...editForm, groupName: e.target.value })}
              required
            />
            <TextField
              label="Grup Açıklaması"
              fullWidth
              multiline
              rows={3}
              value={editForm.groupDescription}
              onChange={(e) => setEditForm({ ...editForm, groupDescription: e.target.value })}
            />
            <TextField
              label="Grup Avatar URL"
              fullWidth
              value={editForm.groupAvatar}
              onChange={(e) => setEditForm({ ...editForm, groupAvatar: e.target.value })}
              placeholder="https://example.com/avatar.jpg"
            />
            <TextField
              label="Okul Adı"
              fullWidth
              value={editForm.groupSchoolName}
              onChange={(e) => setEditForm({ ...editForm, groupSchoolName: e.target.value })}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>İptal</Button>
          <Button
            onClick={handleUpdateGroup}
            color="primary"
            variant="contained"
            disabled={actionLoading || !editForm.groupName || !editForm.groupSchoolName}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Güncelle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Messages Dialog */}
      <Dialog open={messagesDialog} onClose={() => setMessagesDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Grup Mesajları
          <TextField
            fullWidth
            size="small"
            placeholder="Mesaj veya gönderen ara..."
            value={messageSearch}
            onChange={(e) => setMessageSearch(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </DialogTitle>
        <DialogContent dividers>
          {filteredMessages.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              Mesaj bulunamadı.
            </Typography>
          ) : (
            <List>
              {filteredMessages.map((message) => (
                <ListItem
                  key={message.messageId}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, width: '100%' }}>
                    <Avatar src={message.senderProfileImage} sx={{ width: 32, height: 32 }}>
                      {message.senderFullname?.[0]?.toUpperCase() || '?'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="600">
                        {message.senderFullname || 'Bilinmeyen Kullanıcı'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {message.createdAt ? new Date(message.createdAt).toLocaleString('tr-TR') : '-'}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteMessage(message.messageId)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ pl: 5 }}>
                    {message.content || '(Mesaj içeriği yok)'}
                  </Typography>
                  <Chip label={message.messageType || 'text'} size="small" sx={{ ml: 5, mt: 1 }} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessagesDialog(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GroupDetail;

